import { type FloraSettingsNavGroup, type FloraSettingsNavItem, FloraSettingsSection } from './agent-settings.type';

const FLORA_AGENT_BASE_PATH = '/application/flora-agent';
const FLORA_SETTINGS_BASE_PATH = `${FLORA_AGENT_BASE_PATH}/settings`;

/**
 * Absolute paths into the Flora settings area. Link here instead of hand-writing
 * the strings, so a route change only touches this file.
 */
const FLORA_SETTINGS_PATHS = {
  root: FLORA_SETTINGS_BASE_PATH,
  profile: `${FLORA_SETTINGS_BASE_PATH}/${FloraSettingsSection.Profile}`,
  memory: `${FLORA_SETTINGS_BASE_PATH}/${FloraSettingsSection.Memory}`,
  general: `${FLORA_SETTINGS_BASE_PATH}/${FloraSettingsSection.General}`,
  tools: `${FLORA_SETTINGS_BASE_PATH}/${FloraSettingsSection.Tools}`,
  connections: `${FLORA_SETTINGS_BASE_PATH}/${FloraSettingsSection.Connections}`,
} as const;

/**
 * Builds the path of one connection's detail page.
 *
 * @param {string} appId - The connection id (a catalog bucket id such as `google`, or `slack` / `teams`)
 * @returns {string} The absolute detail-page path
 */
function getFloraConnectionPath(appId: string): string {
  return `${FLORA_SETTINGS_PATHS.connections}/${encodeURIComponent(appId)}`;
}

/**
 * Reports whether a pathname is inside the Flora settings area.
 *
 * @param {string} pathname - A router pathname
 * @returns {boolean} True for `/application/flora-agent/settings` and anything below it
 */
function isFloraSettingsPath(pathname: string): boolean {
  return pathname === FLORA_SETTINGS_BASE_PATH || pathname.startsWith(`${FLORA_SETTINGS_BASE_PATH}/`);
}

/**
 * The settings left menu, in display order.
 */
const FLORA_SETTINGS_NAV_GROUPS: FloraSettingsNavGroup[] = [
  {
    label: 'You',
    items: [
      {
        section: FloraSettingsSection.Profile,
        label: 'Profile',
        icon: 'person',
        tileBackground: 'var(--secondary-lightest)',
        tileColor: 'var(--secondary-darkest)',
      },
      {
        section: FloraSettingsSection.Memory,
        label: 'Memory',
        icon: 'psychology',
        tileBackground: 'var(--purple-lightest)',
        tileColor: 'var(--purple-darkest)',
      },
    ],
  },
  {
    label: 'Flora',
    items: [
      {
        section: FloraSettingsSection.General,
        label: 'General',
        icon: 'tune',
        tileBackground: 'var(--sky-lighter)',
        tileColor: 'var(--ink-dark)',
      },
      {
        section: FloraSettingsSection.Tools,
        label: 'Tools',
        icon: 'build',
        tileBackground: 'var(--yellow-lightest)',
        // --yellow-darkest is too faint on --yellow-lightest (1.5:1); ink reads at 11.8:1.
        tileColor: 'var(--ink-dark)',
      },
    ],
  },
  {
    label: 'Connect',
    items: [
      {
        section: FloraSettingsSection.Connections,
        label: 'Connections',
        icon: 'hub',
        tileBackground: 'var(--blue-lightest)',
        tileColor: 'var(--blue-darkest)',
      },
    ],
  },
];

/**
 * Section names used by earlier builds of the settings area, mapped to the
 * section that replaced them, so old links still land somewhere sensible.
 */
const FLORA_SETTINGS_SECTION_ALIASES: Readonly<Record<string, FloraSettingsSection>> = {
  behavior: FloraSettingsSection.General,
  skills: FloraSettingsSection.Tools,
};

/**
 * Looks up the section that replaced a renamed one. Only the alias table's own
 * keys count, so a path segment such as `constructor` isn't mistaken for an
 * alias through the object prototype.
 *
 * @param {string} section - The raw `:section` route segment
 * @returns {FloraSettingsSection | null} The section it was renamed to, or null when it wasn't renamed
 */
function getRenamedFloraSettingsSection(section: string): FloraSettingsSection | null {
  return Object.hasOwn(FLORA_SETTINGS_SECTION_ALIASES, section) ? FLORA_SETTINGS_SECTION_ALIASES[section] : null;
}

/**
 * Finds a section's menu entry (label, icon, tile colours).
 *
 * @param {FloraSettingsSection} section - The section
 * @returns {FloraSettingsNavItem} Its entry in the settings menu
 */
function getFloraSettingsNavItem(section: FloraSettingsSection): FloraSettingsNavItem {
  const item = FLORA_SETTINGS_NAV_GROUPS.flatMap((group) => group.items).find((entry) => entry.section === section);
  if (!item) throw new Error(`No settings menu entry for section "${section}"`);
  return item;
}

const FLORA_SETTINGS_SECTIONS: readonly FloraSettingsSection[] = Object.values(FloraSettingsSection);

/**
 * Narrows a route param to a known settings section.
 *
 * @param {string | undefined} value - The raw `:section` param
 * @returns {value is FloraSettingsSection} True when the value names a section
 */
function isFloraSettingsSection(value: string | undefined): value is FloraSettingsSection {
  return FLORA_SETTINGS_SECTIONS.includes(value as FloraSettingsSection);
}

export {
  FLORA_AGENT_BASE_PATH,
  FLORA_SETTINGS_NAV_GROUPS,
  FLORA_SETTINGS_PATHS,
  getFloraConnectionPath,
  getFloraSettingsNavItem,
  getRenamedFloraSettingsSection,
  isFloraSettingsPath,
  isFloraSettingsSection,
};
